import { useState, useRef, useCallback } from "react";

/**
 * Slider — reusable range slider component
 *
 * Props:
 *   label        {string}   — top-left label text          default: "FREE SERVICABLE KMS"
 *   unit         {string}   — unit shown in value box       default: "KM"
 *   min          {number}   — minimum value                 default: 5
 *   max          {number}   — maximum value                 default: 115
 *   step         {number}   — step increment                default: 10
 *   defaultValue {number}   — initial value                 default: 70
 *   pinColor     {string}   — CSS color for the pin/thumb   default: "#4EB3E8"
 *   trackColor   {string}   — CSS color for filled track    default: "#4EB3E8"
 *   marks        {number[]} — tick marks to display         auto-generated from min/max/step if omitted
 *   onChange     {function} — callback(value) on change
 */
export default function Slider({
  label = "FREE SERVICABLE KMS",
  unit = "KM",
  min = 5,
  max = 115,
  step = 10,
  defaultValue = 70,
  pinColor = "#4EB3E8",
  trackColor = "#4EB3E8",
  marks,
  onChange,
}) {
  const [value, setValue] = useState(defaultValue);
  const trackRef = useRef(null);

  // Auto-generate marks if not provided
  const tickMarks =
    marks ??
    Array.from(
      { length: Math.floor((max - min) / step) + 1 },
      (_, i) => min + i * step,
    );

  const percent = ((value - min) / (max - min)) * 100;

  const handleChange = useCallback(
    (e) => {
      const v = Number(e.target.value);
      setValue(v);
      onChange?.(v);
    },
    [onChange],
  );

  return (
    <div style={styles.card}>
      {/* Header row */}
      <div style={styles.header}>
        <span style={styles.label}>{label}</span>
        <div style={styles.valueBox}>
          {value} {unit}
        </div>
      </div>

      {/* Slider track + pin */}
      <div style={styles.trackWrap} ref={trackRef}>
        {/* Background track */}
        <div style={styles.trackBg} />
        {/* Filled track */}
        <div
          style={{
            ...styles.trackFill,
            width: `${percent}%`,
            background: trackColor,
          }}
        />

        {/* Pin tooltip */}
        <div
          style={{
            ...styles.pin,
            left: `${percent}%`,
            background: pinColor,
          }}
        />
        <div
          style={{
            ...styles.pinNeedle,
            left: `${percent}%`,
            background: pinColor,
          }}
        />

        {/* Native input (invisible, sits over track for interaction) */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleChange}
          style={styles.input}
          aria-label={label}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-valuetext={`${value} ${unit}`}
        />
      </div>

      {/* Tick marks */}
      <div style={styles.marks}>
        {tickMarks.map((mark) => (
          <span
            key={mark}
            style={{
              ...styles.mark,
              fontWeight: mark === value ? "700" : "400",
              color: mark === value ? "#1a1a1a" : "#999",
            }}
          >
            {mark}
          </span>
        ))}
      </div>
    </div>
  );
}

const styles = {
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "16px 20px 12px",
    fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
    userSelect: "none",
    width: "100%",
    maxWidth: "720px",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
  },
  label: {
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.08em",
    color: "#1a1a1a",
    textTransform: "uppercase",
  },
  valueBox: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#1a1a1a",
    border: "1.5px solid #d1d5db",
    borderRadius: "8px",
    padding: "4px 14px",
    minWidth: "72px",
    textAlign: "center",
    letterSpacing: "0.03em",
  },
  trackWrap: {
    position: "relative",
    height: "32px",
    display: "flex",
    alignItems: "center",
    marginBottom: "4px",
  },
  trackBg: {
    position: "absolute",
    left: 0,
    right: 0,
    height: "4px",
    borderRadius: "999px",
    background: "#e5e7eb",
  },
  trackFill: {
    position: "absolute",
    left: 0,
    height: "4px",
    borderRadius: "999px",
    pointerEvents: "none",
    transition: "width 0.05s",
  },
  pin: {
    position: "absolute",
    transform: "translateX(-50%)",
    width: "22px",
    height: "22px",
    borderRadius: "6px",
    top: "2px",
    pointerEvents: "none",
    transition: "left 0.05s",
    zIndex: 2,
  },
  pinNeedle: {
    position: "absolute",
    transform: "translateX(-50%)",
    width: "2px",
    height: "10px",
    top: "24px",
    borderRadius: "999px",
    pointerEvents: "none",
    transition: "left 0.05s",
    zIndex: 2,
  },
  input: {
    position: "absolute",
    left: 0,
    right: 0,
    width: "100%",
    height: "100%",
    opacity: 0,
    cursor: "pointer",
    margin: 0,
    padding: 0,
    zIndex: 3,
  },
  marks: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "10px",
  },
  mark: {
    fontSize: "11px",
    lineHeight: 1,
    transition: "color 0.15s, font-weight 0.15s",
  },
};
